import { useEffect, useMemo, useState } from 'react';
import { api, refreshAccessToken } from '../api/client';
import aideyLogo from '../assets/sidebar-logo.svg';
import { messageFromError } from '../utils/apiError';

const NAVY = '#0C2D48';
const YELLOW = '#E4E69B';
const GREEN = '#27ae60';
const BORDER = '#35454C';
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const cardStyle = {
  background: '#f5f7f9',
  border: `1px solid ${BORDER}`,
  borderRadius: 16,
  width: '100%',
  maxWidth: 440,
  boxShadow: '0 12px 48px rgba(12,45,72,0.28), 0 2px 8px rgba(12,45,72,0.10)',
};

const msgStyle = (type) => ({
  borderRadius: 8,
  padding: '0.75rem 0.9rem',
  fontSize: '0.88rem',
  fontFamily: 'Raleway, sans-serif',
  marginBottom: '1rem',
  ...(type === 'error' && { background: '#fde8e8', border: '1px solid #f5a5a5', color: '#A12710' }),
  ...(type === 'success' && { background: '#eaf6ef', border: '1px solid #6abf85', color: '#1e6645' }),
  ...(type === 'info' && { background: '#e8f0fe', border: '1px solid #93c5fd', color: '#1a56db' }),
});

const primaryLink = {
  display: 'block', textAlign: 'center', padding: '0.75rem', background: YELLOW, borderRadius: 8,
  fontWeight: 700, fontSize: '1rem', fontFamily: 'Poppins, sans-serif', color: '#242629',
  textDecoration: 'none', boxSizing: 'border-box', marginTop: '0.75rem',
};

function DetailRow({ label, value }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '0.6rem 0', borderBottom: '1px solid #e8ecef',
    }}>
      <span style={{ fontSize: '0.82rem', color: '#6a7a7b', fontFamily: 'Raleway, sans-serif', fontWeight: 600 }}>
        {label}
      </span>
      <span style={{ fontSize: '0.88rem', color: NAVY, fontFamily: 'Raleway, sans-serif', fontWeight: 700 }}>
        {value}
      </span>
    </div>
  );
}

export default function AcceptInvite() {
  const token = useMemo(() => {
    return new URLSearchParams(window.location.search).get('token') || '';
  }, []);

  const [status, setStatus] = useState('loading'); // loading | ready | accepting | done | next | error
  const [next, setNext] = useState(null); // Tier 1 #9: what the invitee does to finish joining
  const [linkSent, setLinkSent] = useState('');
  const [invite, setInvite] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMsg('No invitation token found in the link. Please check the email and try again.');
      return;
    }

    let alive = true;
    api(`/api/team/invite/preview?token=${encodeURIComponent(token)}`)
      .then(r => r.json())
      .then(data => {
        if (!alive) return;
        if (data.error) {
          setStatus('error');
          setErrorMsg(
            data.error === 'invite_expired'
              ? 'This invitation has expired or has already been used.'
              : 'This invitation link is not valid.'
          );
        } else {
          setInvite(data.invite);
          setStatus('ready');
        }
      })
      .catch(() => {
        if (!alive) return;
        setStatus('error');
        setErrorMsg('Could not load the invitation. Please try again.');
      });

    return () => { alive = false; };
  }, [token]);

  // Tier 1 #9 (AF-032): Accept records that they said yes. They JOIN when
  // they are signed in as the invited address — now, if they already are,
  // otherwise when they sign in. So: try to resume a session first.
  async function handleAccept() {
    setStatus('accepting');
    try { await refreshAccessToken(); } catch { /* not signed in: fine */ }
    try {
      const res = await api('/api/team/invite/accept', {
        method: 'POST',
        body: JSON.stringify({ token }),
      });
      const data = await res.json();
      if (data.joined) {
        setStatus('done');
        setTimeout(() => { window.location.href = data.redirect || '/'; }, 1200);
        return;
      }
      setNext(data);
      setStatus('next');
    } catch (err) {
      setStatus('error');
      setErrorMsg(messageFromError(err, 'Could not accept the invitation. Please try again.'));
    }
  }

  async function sendSetPasswordLink() {
    try {
      await api('/api/auth/forgot-password', { method: 'POST', body: JSON.stringify({ email: next.email }) });
      setLinkSent(`We've emailed a link to ${next.email}. Set your password, then sign in, you'll join the team as soon as you do.`);
    } catch (err) {
      setErrorMsg(messageFromError(err, 'Could not send the link. Please try again.'));
    }
  }

  const daysLeft = invite
    ? Math.max(0, Math.ceil((new Date(invite.expiresAt) - Date.now()) / 86400000))
    : null;

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: `linear-gradient(150deg, ${NAVY} 0%, #1a4a7a 50%, #0a2038 100%)`,
      padding: '1rem',
      fontFamily: 'Raleway, sans-serif',
    }}>
      <div style={cardStyle}>
        {/* Logo */}
        <div style={{ padding: '2rem 2rem 1.25rem', textAlign: 'center' }}>
          <img
            src={aideyLogo}
            alt="Aidey"
            style={{
              height: 52, width: 'auto', display: 'block', margin: '0 auto',
              border: 'none', outline: 'none', boxShadow: 'none', background: 'transparent',
            }}
          />
        </div>

        <div style={{ borderTop: '1px solid #c5cdd4', margin: '0 1.5rem' }} />

        <div style={{ padding: '1.75rem 2rem 2rem' }}>

          {/* Loading */}
          {status === 'loading' && (
            <div style={msgStyle('info')}>Loading invitation details…</div>
          )}

          {/* Error */}
          {status === 'error' && (
            <>
              <h2 style={{ margin: '0 0 0.75rem', fontSize: '1.15rem', color: NAVY, fontFamily: 'Poppins, sans-serif', fontWeight: 700 }}>
                Invitation unavailable
              </h2>
              <div style={msgStyle('error')}>⚠ {errorMsg}</div>
              <a href="/" style={{
                display: 'block', width: '100%', textAlign: 'center', padding: '0.75rem',
                background: YELLOW, borderRadius: 8, fontWeight: 700, fontSize: '1rem',
                fontFamily: 'Poppins, sans-serif', color: '#242629', textDecoration: 'none',
                boxSizing: 'border-box',
              }}>
                Go to sign in
              </a>
            </>
          )}

          {/* Accepted — one step left (Tier 1 #9) */}
          {status === 'next' && next && (
            <div data-testid="invite-next">
              <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.15rem', color: NAVY, fontFamily: 'Poppins, sans-serif', fontWeight: 700 }}>
                Accepted, one step left
              </h2>
              {next.next === 'create_account' && (
                <>
                  <p style={{ fontSize: '0.88rem', color: '#4a5a5b', lineHeight: 1.5 }}>
                    Create your Aidey account with <strong>{next.email}</strong>. You'll join the team as soon as you sign in.
                  </p>
                  <a href={`/register?email=${encodeURIComponent(next.email)}&inviteToken=${encodeURIComponent(token)}`} style={primaryLink}>Create my account</a>
                </>
              )}
              {next.next === 'sign_in' && (
                <>
                  <p style={{ fontSize: '0.88rem', color: '#4a5a5b', lineHeight: 1.5 }}>
                    Sign in as <strong>{next.email}</strong> to finish joining.
                  </p>
                  <a href={`/login?email=${encodeURIComponent(next.email)}`} style={primaryLink}>Sign in</a>
                </>
              )}
              {next.next === 'set_password' && (
                <>
                  <p style={{ fontSize: '0.88rem', color: '#4a5a5b', lineHeight: 1.5 }}>
                    There's an Aidey account for <strong>{next.email}</strong> that has never been signed in to. Set a password for it, then sign in to finish joining.
                  </p>
                  {linkSent
                    ? <div style={msgStyle('success')}>{linkSent}</div>
                    : <button type="button" onClick={sendSetPasswordLink} style={{ ...primaryLink, border: 'none', cursor: 'pointer', width: '100%' }}>Email me a link to set my password</button>}
                  {errorMsg && <div style={msgStyle('error')}>⚠ {errorMsg}</div>}
                </>
              )}
            </div>
          )}

          {/* Done */}
          {status === 'done' && (
            <div style={{ textAlign: 'center', padding: '1.5rem 0' }}>
              <div style={{
                width: 56, height: 56, borderRadius: '50%', background: '#eaf6ef',
                border: `2px solid ${GREEN}`, display: 'flex', alignItems: 'center',
                justifyContent: 'center', margin: '0 auto 1rem', fontSize: '1.5rem',
              }}>
                ✓
              </div>
              <p style={{ fontWeight: 700, fontSize: '1rem', color: NAVY, margin: '0 0 0.4rem', fontFamily: 'Poppins, sans-serif' }}>
                You're in!
              </p>
              <p style={{ fontSize: '0.88rem', color: '#4a5a5b', margin: 0 }}>
                Redirecting you to Aidey…
              </p>
            </div>
          )}

          {/* Ready / Accepting */}
          {(status === 'ready' || status === 'accepting') && invite && (
            <>
              <h2 style={{ margin: '0 0 0.35rem', fontSize: '1.15rem', color: NAVY, fontFamily: 'Poppins, sans-serif', fontWeight: 700 }}>
                You've been invited
              </h2>
              <p style={{ margin: '0 0 1.5rem', fontSize: '0.84rem', color: '#4a5a5b', lineHeight: 1.5 }}>
                <strong>{invite.inviterName}</strong> has invited you to join their workspace on Aidey.
              </p>

              {/* Invite detail card */}
              <div style={{
                background: '#fff', border: '1px solid #e0e4e8', borderRadius: 10,
                padding: '0.25rem 1rem 0.5rem', marginBottom: '1.5rem',
              }}>
                <DetailRow label="Workspace" value={invite.teamName} />
                <DetailRow label="Invited by" value={invite.inviterName} />
                <DetailRow label="Your email" value={invite.email} />
                <DetailRow label="Role" value={invite.role.charAt(0).toUpperCase() + invite.role.slice(1)} />
                <div style={{ padding: '0.6rem 0' }}>
                  <span style={{ fontSize: '0.78rem', color: daysLeft <= 1 ? '#c0392b' : '#6a7a7b', fontFamily: 'Raleway, sans-serif' }}>
                    Expires in {daysLeft} day{daysLeft !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>

              <button
                onClick={handleAccept}
                disabled={status === 'accepting'}
                style={{
                  width: '100%', padding: '0.75rem',
                  background: status === 'accepting' ? '#c8cabb' : YELLOW,
                  border: 'none', borderRadius: 8, fontWeight: 700, fontSize: '1rem',
                  fontFamily: 'Poppins, sans-serif',
                  cursor: status === 'accepting' ? 'not-allowed' : 'pointer',
                  color: '#242629', transition: 'background 0.2s', marginBottom: '1rem',
                }}
              >
                {status === 'accepting' ? 'Accepting…' : 'Accept invitation'}
              </button>

              <p style={{ textAlign: 'center', fontSize: '0.82rem', color: '#4a5a5b', margin: 0 }}>
                Wrong account?{' '}
                <a href="/" style={{ color: NAVY, fontWeight: 700, textDecoration: 'none' }}>
                  Sign in with a different account
                </a>
              </p>
            </>
          )}

        </div>
      </div>
    </div>
  );
}
